<template>
  <div class="menu-manager-container">
    <!-- ===== 左侧：菜单排序（拖拽即改顺序，落位咬死不回弹） ===== -->
    <el-card class="box-card sort-card" shadow="never">
      <div slot="header" class="clearfix">
        <span>菜单排序</span>
        <span class="sort-card__hint">按住左侧握把拖动，松手即落位</span>
      </div>

      <div v-if="!sortableMenus.length" class="sort-empty">暂无菜单数据</div>

      <ul v-else ref="sortList" class="sort-list">
        <li
          v-for="(item, index) in sortableMenus"
          :key="item.id"
          class="sort-item"
          :class="{
            'sort-item--dragging': dragIndex === index,
            'sort-item--placeholder': placeholderIndex === index && dragIndex !== null
          }"
          :data-index="index"
        >
          <span
            class="sort-item__handle"
            title="拖动排序"
            @mousedown.prevent="startDrag(index, $event)"
            @touchstart.passive="startDrag(index, $event)"
          >
            <i class="el-icon-rank"></i>
          </span>
          <span class="sort-item__title">{{ item.title }}</span>
          <span class="sort-item__path">{{ item.path || '目录' }}</span>
        </li>
      </ul>

      <div v-if="dragIndex !== null" class="sort-drag-ghost" :style="ghostStyle">
        {{ sortableMenus[dragIndex] && sortableMenus[dragIndex].title }}
      </div>
    </el-card>

    <!-- ===== 右侧：新增菜单表单 ===== -->
    <el-card class="box-card form-card" shadow="never">
      <div slot="header" class="clearfix">
        <span>新增菜单模块</span>
      </div>

      <el-form
        ref="menuForm"
        :model="form"
        :rules="rules"
        label-width="120px"
      >
        <el-form-item label="菜单级别" prop="level">
          <el-radio-group v-model="form.level" @change="onLevelChange">
            <el-radio :label="1">一级菜单</el-radio>
            <el-radio :label="2">二级菜单</el-radio>
            <el-radio :label="3">三级菜单</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="菜单类型" prop="menuType">
          <el-radio-group v-model="form.menuType" @change="onMenuTypeChange">
            <el-radio label="menu">菜单</el-radio>
            <el-radio label="directory" :disabled="form.level === 3">目录</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item v-if="form.level >= 2" label="一级菜单" prop="firstLevelMenu">
          <el-select
            v-model="form.firstLevelMenu"
            placeholder="请选择一级菜单"
            style="width: 100%"
            @change="onFirstLevelChange"
          >
            <el-option
              v-for="item in firstLevelMenus"
              :key="item.id"
              :label="item.title"
              :value="item.id"
            />
          </el-select>
        </el-form-item>

        <el-form-item v-if="form.level === 3" label="二级菜单" prop="secondLevelMenu">
          <el-select
            v-model="form.secondLevelMenu"
            placeholder="请选择二级菜单"
            style="width: 100%"
          >
            <el-option
              v-for="item in secondLevelMenus"
              :key="item.id"
              :label="item.title"
              :value="item.id"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="菜单名称" prop="menuName">
          <el-input
            v-model.trim="form.menuName"
            maxlength="20"
            show-word-limit
            placeholder="请输入菜单名称"
          />
        </el-form-item>

        <el-form-item v-if="form.menuType === 'menu'" label="路由地址" prop="path">
          <el-input
            v-model.trim="form.path"
            placeholder="/tools/new-page"
            @input="generateComponentPath"
          />
        </el-form-item>

        <el-form-item v-if="form.menuType === 'menu' && form.componentPath" label="组件路径">
          <el-alert
            :title="displayComponentPath"
            type="info"
            :closable="false"
            show-icon
          />
        </el-form-item>

        <el-form-item>
          <el-button type="primary" :loading="loading" @click="submitForm">
            新增菜单
          </el-button>
          <el-button @click="resetForm">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>
  </div>
</template>

<script>
import { initDynamicRoutes } from '@/router'
import request from '@/api/request'
import { getResponseMessage, getResponsePayload, isSuccessResponse } from '@/utils/apiResponse'

function findMenuById(menuTree, targetId) {
  for (const menu of menuTree) {
    if (menu.id === targetId) {
      return menu
    }

    const children = Array.isArray(menu.children) ? menu.children : []
    if (children.length) {
      const result = findMenuById(children, targetId)
      if (result) {
        return result
      }
    }
  }

  return null
}

export default {
  name: 'MenuManager',
  data() {
    const validatePath = (rule, value, callback) => {
      if (this.form.menuType === 'directory') {
        callback()
        return
      }

      const routePath = String(value || '').trim()
      if (!routePath) {
        callback(new Error('请输入路由地址'))
        return
      }

      if (!/^\/[A-Za-z0-9/_-]+$/.test(routePath)) {
        callback(new Error('路由地址格式不正确'))
        return
      }

      callback()
    }

    return {
      form: {
        level: 1,
        menuType: 'menu',
        firstLevelMenu: '',
        secondLevelMenu: '',
        menuName: '',
        path: '',
        componentPath: ''
      },
      loading: false,
      menuList: [],
      // ===== 拖拽排序状态 =====
      // dragIndex：当前被拖起的条目下标，null 表示未在拖拽
      // placeholderIndex：拖到哪个位置，会在该位置显示占位缺口
      dragIndex: null,
      placeholderIndex: null,
      // ghost 跟随指针的浮层坐标与尺寸
      ghostX: 0,
      ghostY: 0,
      ghostWidth: 0,
      ghostHeight: 0,
      // 拖拽起始时的指针 Y 与测得每一行的高度（拖拽中不再重排测量）
      dragStartY: 0,
      rowHeight: 0,
      rules: {
        level: [
          { required: true, message: '请选择菜单级别', trigger: 'change' }
        ],
        menuType: [
          { required: true, message: '请选择菜单类型', trigger: 'change' }
        ],
        firstLevelMenu: [
          { required: true, message: '请选择一级菜单', trigger: 'change' }
        ],
        secondLevelMenu: [
          { required: true, message: '请选择二级菜单', trigger: 'change' }
        ],
        menuName: [
          { required: true, message: '请输入菜单名称', trigger: 'blur' },
          { min: 2, max: 20, message: '长度应为 2 到 20 个字符', trigger: 'blur' }
        ],
        path: [
          { validator: validatePath, trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    firstLevelMenus() {
      return this.menuList.filter(item => !item.parentId)
    },
    /**
     * 可排序列表：一级菜单按展示顺序展开，并带上各自的子菜单，
     * 形成「一级 → 其子级」的扁平序列，拖拽时只在这一层做位置交换。
     */
    sortableMenus() {
      return this.firstLevelMenus
    },
    /** 跟随指针的拖拽浮层样式：用 transform 而不是 top/left，走合成层不触发重排 */
    ghostStyle() {
      return {
        width: `${this.ghostWidth}px`,
        height: `${this.ghostHeight}px`,
        transform: `translate3d(${this.ghostX}px, ${this.ghostY}px, 0)`,
      }
    },
    secondLevelMenus() {
      if (!this.form.firstLevelMenu) {
        return []
      }

      const firstMenu = this.firstLevelMenus.find(item => item.id === this.form.firstLevelMenu)
      return firstMenu && Array.isArray(firstMenu.children) ? firstMenu.children : []
    },
    displayComponentPath() {
      return this.form.componentPath
        ? `frontend/src/views/${this.form.componentPath}`
        : ''
    }
  },
  created() {
    this.loadMenuDataFromApi()
  },
  beforeDestroy() {
    // 页面切走时若仍在拖拽，必须解绑全局监听，否则会残留并报错
    this.stopDrag()
  },
  methods: {
    /**
     * 开始拖拽。
     * 手感要点：拖起的瞬间就把原始条目变成占位、浮层跟手（transform 零延迟），
     * 全程不依赖 CSS transition 位移，避免「跟手慢半拍」。
     * @param {number} index 被拖起条目的下标
     * @param {MouseEvent|TouchEvent} evt 触发事件
     */
    startDrag(index, evt) {
      if (this.dragIndex !== null) return

      const node = this.$refs.sortList && this.$refs.sortList.children[index]
      if (!node) return

      const rect = node.getBoundingClientRect()
      const point = this.getPoint(evt)

      this.dragIndex = index
      this.placeholderIndex = index
      this._ghostOffsetY = point.y - rect.top
      this.dragStartY = point.y
      this.rowHeight = rect.height + this.getRowGap()
      this.ghostWidth = rect.width
      this.ghostHeight = rect.height
      this.ghostX = rect.left
      this.ghostY = rect.top
      // 横向锁定位置：拖动过程中 ghostX 始终等于起拖时的左边缘
      this._ghostLeft = rect.left

      // 记录原始顺序，用于松手时的落位判定
      this._originOrder = this.sortableMenus.slice()

      window.addEventListener('mousemove', this.onDragMove, { passive: true })
      window.addEventListener('mouseup', this.onDragEnd)
      window.addEventListener('touchmove', this.onDragMove, { passive: false })
      window.addEventListener('touchend', this.onDragEnd)
      window.addEventListener('touchcancel', this.onDragEnd)
    },

    /** 统一取指针坐标（鼠标 / 触摸） */
    getPoint(evt) {
      const t = evt.touches && evt.touches[0]
      const p = t || evt.changedTouches && evt.changedTouches[0] || evt
      return { x: p.clientX, y: p.clientY }
    },

    /** 读取列表行间距，用于计算换位阈值 */
    getRowGap() {
      const list = this.$refs.sortList
      if (!list || typeof window === 'undefined') return 0
      const gap = parseFloat(window.getComputedStyle(list).rowGap || '0')
      return Number.isFinite(gap) ? gap : 0
    },

    /** 拖拽移动：浮层立刻跟手，并按位移算目标插入位置 */
    onDragMove(evt) {
      if (this.dragIndex === null) return
      // 触摸拖动时阻止页面跟着滚
      if (evt.cancelable) evt.preventDefault()

      const point = this.getPoint(evt)
      this.ghostY = point.y - this._ghostOffsetY
      // 横向锁在起拖时的左边缘：拖着上下走时浮层不左右飘，观感更稳
      this.ghostX = this._ghostLeft

      const delta = point.y - this.dragStartY
      const steps = this.rowHeight ? Math.round(delta / this.rowHeight) : 0
      const target = Math.min(
        this.sortableMenus.length - 1,
        Math.max(0, this.dragIndex + steps)
      )
      this.placeholderIndex = target
    },

    /**
     * 松手落位：直接交换数据顺序，动画只给 120ms 收尾，绝不回弹晃悠。
     * 落位后立即把新顺序提交后端，刷新页面不会回到旧顺序。
     */
    async onDragEnd() {
      if (this.dragIndex === null) {
        this.stopDrag()
        return
      }

      const from = this.dragIndex
      const to = this.placeholderIndex
      const changed = to !== null && from !== to
      let reorderedIds = []

      if (changed) {
        const list = this.sortableMenus.slice()
        const [moved] = list.splice(from, 1)
        list.splice(to, 0, moved)
        reorderedIds = list.map(item => item.id)
        // 原地写回，保持响应式
        this.menuList.splice(0, this.menuList.length, ...list)
      }
      this.stopDrag()

      if (!changed) return

      // 先给即时反馈，不等网络往返，避免拖完一顿才提示
      this.$toast.success('菜单顺序已更新')

      try {
        await request.post('/api/menu/reorder', {
          groups: [{ parentId: null, ids: reorderedIds }]
        })
        // 顺序已落库，刷新动态路由让侧边栏立刻跟随
        await initDynamicRoutes(true)
        window.dispatchEvent(new Event('menu-updated'))
      } catch (error) {
        console.error('Failed to persist menu order:', error)
        this.$toast.error('排序保存失败，已恢复原顺序')
        // 持久化失败时回滚到拖拽前顺序，避免界面与实际数据不一致
        await this.loadMenuDataFromApi()
      }
    },

    /** 清理拖拽状态与全局监听 */
    stopDrag() {
      window.removeEventListener('mousemove', this.onDragMove)
      window.removeEventListener('mouseup', this.onDragEnd)
      window.removeEventListener('touchmove', this.onDragMove)
      window.removeEventListener('touchend', this.onDragEnd)
      window.removeEventListener('touchcancel', this.onDragEnd)
      this.dragIndex = null
      this.placeholderIndex = null
      this._originOrder = null
    },

    async loadMenuDataFromApi() {
      try {
        // 响应拦截器在 code=0 时已返回 res.data，此处 response 即为菜单数组
        const response = await request.get('/api/menu/list')
        console.log('response',response)
        if (Array.isArray(response)) {
          this.menuList = response
          return
        }

        // 兼容拦截器未解包的情况（如返回完整响应体）
        if (isSuccessResponse(response)) {
          this.menuList = getResponsePayload(response, [])
          return
        }

        this.$toast.error(getResponseMessage(response, '获取菜单数据失败'))
      } catch (error) {
        console.error('Failed to load menu data:', error)
        this.$toast.error('获取菜单数据失败，请稍后重试')
      }
    },
    onLevelChange() {
      if (this.form.level === 3 && this.form.menuType === 'directory') {
        this.form.menuType = 'menu'
      }

      this.form.firstLevelMenu = ''
      this.form.secondLevelMenu = ''

      this.$nextTick(() => {
        if (this.$refs.menuForm) {
          this.$refs.menuForm.clearValidate()
        }
      })
    },
    onMenuTypeChange(value) {
      if (value === 'directory') {
        this.form.path = ''
        this.form.componentPath = ''
      } else {
        this.generateComponentPath()
      }

      this.$nextTick(() => {
        if (this.$refs.menuForm) {
          this.$refs.menuForm.clearValidate(['path'])
        }
      })
    },
    onFirstLevelChange() {
      this.form.secondLevelMenu = ''
    },
    generateComponentPath() {
      if (this.form.menuType !== 'menu') {
        this.form.componentPath = ''
        return
      }

      const cleanPath = String(this.form.path || '')
        .trim()
        .replace(/^\/+/, '')
        .replace(/\/+$/, '')

      this.form.componentPath = cleanPath ? `${cleanPath}/index.vue` : ''
    },
    resolveParentId() {
      if (this.form.level === 1) {
        return null
      }

      if (this.form.level === 2) {
        const parentMenu = findMenuById(this.menuList, this.form.firstLevelMenu)
        return parentMenu ? parentMenu.id : null
      }

      const secondLevelMenu = findMenuById(this.menuList, this.form.secondLevelMenu)
      return secondLevelMenu ? secondLevelMenu.id : null
    },
    async submitForm() {
      this.$refs.menuForm.validate(async valid => {
        if (!valid) {
          return
        }

        this.loading = true

        try {
          const response = await request.post('/api/menu/create', {
            title: this.form.menuName.trim(),
            type: this.form.menuType,
            path: this.form.menuType === 'menu' ? this.form.path.trim() : '',
            parentId: this.resolveParentId(),
            componentPath: this.form.menuType === 'menu' ? this.form.componentPath : ''
          })

          // 拦截器在 code=0 时返回 res.data，失败时已 reject 并弹错，走到这里即为成功

          await this.loadMenuDataFromApi()
          await initDynamicRoutes(true)
          window.dispatchEvent(new Event('menu-updated'))

          this.$toast.success('新增成功')
          this.resetForm()
        } catch (error) {
          console.error('Failed to create menu:', error)
          this.$toast.error(
            error.response?.data?.message ||
            error.response?.data?.msg ||
            error.message ||
            '新增菜单失败'
          )
        } finally {
          this.loading = false
        }
      })
    },
    resetForm() {
      this.form = {
        level: 1,
        menuType: 'menu',
        firstLevelMenu: '',
        secondLevelMenu: '',
        menuName: '',
        path: '',
        componentPath: ''
      }

      this.$nextTick(() => {
        if (this.$refs.menuForm) {
          this.$refs.menuForm.clearValidate()
        }
      })
    }
  }
}
</script>

<style scoped>
.menu-manager-container {
  /* 左右双栏：左侧排序、右侧新建表单。整体撑满内容区，滚动下沉到各卡片内部 */
  height: 100%;
  box-sizing: border-box;
  padding: 0;
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

/* 两栏等宽，各自内部滚动 */
.sort-card,
.form-card {
  flex: 1 1 0;
  min-width: 0;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  /* 顶部对齐时两栏各自呼吸，高度由内容决定 */
  align-self: stretch;
}

/* 卡片头部不参与滚动，正文区独立滚动 */
.sort-card >>> .el-card__header,
.form-card >>> .el-card__header {
  flex: 0 0 auto;
}

/* 卡片正文独立滚动，需要给 flex 容器一个可收缩的容器 */
.sort-card >>> .el-card__body,
.form-card >>> .el-card__body {
  flex: 1 1 auto;
  overflow: auto;
  min-height: 0;
}

/* 窄屏下退回上下堆叠，避免两栏被挤成细条 */
@media (max-width: 1100px) {
  .menu-manager-container {
    flex-direction: column;
  }

  .sort-card,
  .form-card {
    width: 100%;
    flex: 0 0 auto;
    max-height: none;
    align-self: auto;
  }
}

.clearfix:before,
.clearfix:after {
  display: table;
  content: "";
}

.clearfix:after {
  clear: both;
}

.el-form {
  margin-top: 0;
}

.el-select {
  width: 100%;
}

.el-alert {
  margin-top: 10px;
}

/* ================= 菜单排序（拖拽） ================= */
.sort-card__hint {
  margin-left: 12px;
  font-size: 12px;
  color: #909399;
  font-weight: normal;
}

.sort-empty {
  padding: 24px 0;
  text-align: center;
  color: #909399;
  font-size: 13px;
}

.sort-list {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.sort-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  background: var(--surface-1, #fff);
  border-radius: 8px;
  box-shadow: var(--shadow-surface-1, 0 1px 2px rgba(31, 45, 61, 0.06));
  /* 只在「非拖拽」状态下启用位移过渡：松手落位时有 120ms 的快速收尾，
     拖动过程中则完全不插过渡，保证跟手零延迟。 */
  transition: transform 120ms var(--ease-out-soft, cubic-bezier(0.25, 0.1, 0.25, 1)),
              box-shadow 120ms ease;
  user-select: none;
}

/* 拖起时：原条目转为虚线占位，不再参与视觉，但保留空间防止列表塌缩 */
.sort-item--dragging {
  opacity: 0;
}

.sort-item--placeholder {
  background: var(--surface-2, #f4f4f5);
  box-shadow: none;
  outline: 1px dashed rgba(64, 158, 255, 0.5);
  outline-offset: -1px;
}

.sort-item__handle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 5px;
  color: #909399;
  cursor: grab;
  touch-action: none; /* 触屏拖动时不让页面跟着滚 */
  transition: color 120ms ease, background-color 120ms ease;
}

.sort-item__handle:hover {
  color: var(--brand-color, #409eff);
  background: var(--surface-2, #f4f4f5);
}

.sort-item__handle:active {
  cursor: grabbing;
}

.sort-item__title {
  flex: 1;
  font-size: 14px;
  color: var(--surface-text-1, #303133);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sort-item__path {
  font-size: 12px;
  color: #909399;
  font-variant-numeric: tabular-nums;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 40%;
}

/* 跟随指针的浮层：transform 定位走合成层，拖动时零重排。
   轻微倾斜 + 阴影抬升，给出「离地」的手感，全程无 transition（否则会跟手慢半拍）。 */
.sort-drag-ghost {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 3000;
  display: flex;
  align-items: center;
  padding: 0 14px;
  box-sizing: border-box;
  background: var(--surface-1, #fff);
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(31, 45, 61, 0.18), 0 2px 6px rgba(31, 45, 61, 0.1);
  transform-origin: center;
  pointer-events: none;
  font-size: 14px;
  color: var(--surface-text-1, #303133);
  will-change: transform;
  /* 刻意不写 transition：浮层必须绝对跟手。
     一旦给 transform 加过渡，指针移动到浮层跟随之间就会产生
     几十毫秒的滞后，手感立刻「变黏」。 */
}

@media (prefers-reduced-motion: reduce) {
  .sort-item {
    transition: none;
  }
}
</style>
